"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Sidebar } from "@/components/layout/sidebar";
import { Navbar } from "@/components/layout/navbar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/auth-store";
import { useUIStore } from "@/stores/ui-store";
import { cn } from "@/lib/utils/cn";
import { authApi } from "@/lib/api/auth";

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const { accessToken, user, setUser, logout } = useAuthStore();
  const { sidebarCollapsed } = useUIStore();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isHydrated, setIsHydrated] = React.useState(false);

  React.useEffect(() => {
    setIsHydrated(true);
  }, []);

  React.useEffect(() => {
    const checkAuth = async () => {
      if (!isHydrated) return;

      if (!accessToken) {
        logout();
        router.replace("/login");
        return;
      }

      if (user) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await authApi.getCurrentUser();
        setUser(userData);
        setIsLoading(false);
      } catch (error) {
        console.error("Auth check failed:", error);
        logout();
        router.replace("/login");
      }
    };

    checkAuth();
  }, [isHydrated, accessToken, user, router, setUser, logout]);

  React.useEffect(() => {
    if (!isLoading && !accessToken) {
      router.replace("/login");
    }
  }, [isLoading, accessToken, router]);

  if (isLoading || !accessToken) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={50}>
      <div className="layout">
        <Sidebar />
        <div
          className={cn(
            "layout__wrap flex flex-col",
            sidebarCollapsed && "layout__wrap--slim"
          )}
        >
          <div className="layout__navbar">
            <Navbar />
          </div>
          <main className="layout__content flex-1">{children}</main>
        </div>
      </div>
    </TooltipProvider>
  );
}
